import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import type { RouteRecordName } from 'vue-router/auto';
import { DocumentTypesUpdated } from '@/api/events';

/**
 * Store with miscellaneous set of functions.
 */
export const useMiscStore = defineStore('miscStore', () => {
    // #region global loading indicator
    const loaders = ref(0);
    const runLoader = <T extends Array<unknown>, U>(
        fn: (...args: T) => Promise<U> | U,
        opts: { minLoading?: number } = {},
    ) => {
        return async (...args: T) => {
            try {
                loaders.value++;
                return (
                    await Promise.all([
                        Promise.resolve(fn(...args)),
                        new Promise(r => setTimeout(r, opts.minLoading ?? 300)),
                    ])
                )[0];
            } finally {
                loaders.value--;
            }
        };
    };

    const isLoading = computed(() => loaders.value > 0);

    // #endregion

    // #region route transition tracker
    const isRouteLoading = ref(false);
    const routeVisiting = ref<RouteRecordName | null>();
    // #endregion

    // #region languages

    const useOnceLoader = <T>(loader: () => Promise<T[]>) => {
        const internal = ref<T[]>();
        const load = useLoading().withLoading(async () => {
            if (internal.value != null) return;
            const result = await loader();
            internal.value = result;
            return result;
        });

        const data = computed(() => {
            if (internal.value == null) {
                load();
                return [];
            }
            return internal.value;
        });

        const setValue = (value: T[]) => {
            internal.value = value;
        };

        return {
            data,
            load,
            setValue,
        };
    };

    const { data: languages } = useOnceLoader(() => api.listLanguages().then(d => d.data));
    const { data: currencies } = useOnceLoader(() => api.listCurrencies().then(d => d.data));
    const { data: documentTypes, setValue: setDocumentTypes } = useOnceLoader(() =>
        api.listDocumentTypes().then(d => d.data),
    );
    const { data: qualificationLevels } = useOnceLoader(() => api.listQualificationLevels().then(d => d.data));
    const { data: programLevels } = useOnceLoader(() => api.listProgramLevels().then(d => d.data));

    const languagesSelect = computed(() =>
        languages.value.map(l => ({
            label: l.name,
            value: l.alpha2,
        })),
    );

    const currenciesSelect = computed(() =>
        currencies.value.map(c => ({
            label: c.name,
            value: c.code,
        })),
    );

    const paymentCurrencies = computed(() => currencies.value.filter(c => c.payments_enabled));

    const paymentCurrenciesSelect = computed(() =>
        paymentCurrencies.value.map(c => ({
            label: c.name,
            value: c.code,
        })),
    );

    const documentTypesSelect = computed(() =>
        documentTypes.value.map(d => ({
            label: d.title,
            value: d.id,
            disabled: !d.enabled,
        })),
    );

    const programLevelsSelect = computed(() =>
        programLevels.value.map(d => ({
            label: d.title,
            value: d.id,
            disabled: !d.enabled,
        })),
    );

    const qualificationLevelsSelect = computed(() =>
        qualificationLevels.value.map(d => ({
            label: d.title,
            value: d.id,
            disabled: !d.enabled,
        })),
    );

    const monthSelect = [
        {
            label: 'January',
            value: 1,
        },
        {
            label: 'February',
            value: 2,
        },
        {
            label: 'March',
            value: 3,
        },
        {
            label: 'April',
            value: 4,
        },
        {
            label: 'May',
            value: 5,
        },
        {
            label: 'June',
            value: 6,
        },
        {
            label: 'July',
            value: 7,
        },
        {
            label: 'August',
            value: 8,
        },
        {
            label: 'September',
            value: 9,
        },
        {
            label: 'October',
            value: 10,
        },
        {
            label: 'November',
            value: 11,
        },
        {
            label: 'December',
            value: 12,
        },
    ];

    const assessmentLevels = [
        {
            label: '1',
            value: 1,
        },
        {
            label: '2',
            value: 2,
        },
        {
            label: '3',
            value: 3,
        },
        {
            label: '4',
            value: 4,
        },
        {
            label: '5',
            value: 5,
        },
    ];

    const monthsToNames = <T extends number[] | number>(months: T): T extends number ? string : string[] =>
        !Array.isArray(months)
            ? ((monthSelect.find(ms => ms.value === months)?.label ?? '-') as any)
            : (months
                  .map(m => monthSelect.find(ms => ms.value === m)?.label)
                  .filter((m): m is string => m != null) as any);

    const clientStore = useClientStore();
    useChannel(
        computed(() => (clientStore.isLoggedIn ? 'document-types' : undefined)),
        DocumentTypesUpdated,
        data => {
            if (data) {
                setDocumentTypes(data.data);
            }
        },
    );

    const ongoingNetworkCalls = ref(0);

    // #endregion

    return {
        runLoader,
        loaders: readonly(loaders),
        isLoading,
        isRouteLoading,
        routeVisiting,
        languages,
        currencies,
        documentTypes,
        programLevels,
        languagesSelect,
        currenciesSelect,
        paymentCurrencies,
        paymentCurrenciesSelect,
        documentTypesSelect,
        programLevelsSelect,
        monthSelect,
        monthsToNames,
        assessmentLevels,
        qualificationLevels,
        qualificationLevelsSelect,
        ongoingNetworkCalls,
    };
});
